/**
 * companyTime — the single frontend utility for rendering dates/times.
 *
 * The whole app must display times in the COMPANY's timezone (a required
 * company setting), never the viewer's device zone. Timestamps come from
 * the API in UTC ISO; these helpers format them in the company zone and,
 * where useful, append a short zone label (e.g. "3:00 PM EST").
 *
 * Usage in a component:
 *   const tz = useCompanyTz();
 *   fmtDateTime(iso, tz)          // "Sep 8, 2026, 3:00 PM"
 *   fmtDateTimeZoned(iso, tz)     // "Sep 8, 2026, 3:00 PM EST"
 *   fmtDate(iso, tz)              // "Sep 8, 2026"
 *   fmtTimeZoned(iso, tz)         // "3:00 PM EST"
 *
 * In non-component code, pass the tz explicitly (get it from the loaded
 * company doc or the auth user's `company_timezone`).
 */
import { useAuth } from '@/contexts/AuthContext';

export const DEFAULT_TZ = 'America/New_York';

// Module-level "active" company timezone. AuthContext sets this once the
// signed-in user is loaded, so PURE formatters (leadConstants, files
// helpers, lib/tz, etc.) can render in the company zone WITHOUT every
// caller threading a tz prop. Components should still prefer useCompanyTz().
let _activeTz = DEFAULT_TZ;

export function setActiveCompanyTz(tz) {
  if (typeof tz === 'string' && tz) _activeTz = tz;
}

export function getActiveCompanyTz() {
  return _activeTz;
}

export function resolveTz(tz) {
  return typeof tz === 'string' && tz ? tz : _activeTz;
}

function _toDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Core formatter — Intl.DateTimeFormat pinned to the company zone. */
export function formatInTz(value, tz, options = {}) {
  const d = _toDate(value);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: resolveTz(tz), ...options }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', { timeZone: DEFAULT_TZ, ...options }).format(d);
  }
}

export function fmtDate(value, tz, options) {
  return formatInTz(value, tz, { year: 'numeric', month: 'short', day: 'numeric', ...(options || {}) });
}

export function fmtDateShort(value, tz, options) {
  return formatInTz(value, tz, { month: 'short', day: 'numeric', ...(options || {}) });
}

export function fmtTime(value, tz, options) {
  return formatInTz(value, tz, { hour: 'numeric', minute: '2-digit', ...(options || {}) });
}

export function fmtDateTime(value, tz, options) {
  return formatInTz(value, tz, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', ...(options || {}),
  });
}

/** Short zone label for a moment, e.g. "EST"/"EDT" (DST-aware). */
export function zoneAbbr(value, tz) {
  const d = _toDate(value) || new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: resolveTz(tz), timeZoneName: 'short',
    }).formatToParts(d);
    const p = parts.find((x) => x.type === 'timeZoneName');
    return p ? p.value : '';
  } catch {
    return '';
  }
}

export function fmtTimeZoned(value, tz, options) {
  return formatInTz(value, tz, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', ...(options || {}) });
}

export function fmtDateTimeZoned(value, tz, options) {
  return formatInTz(value, tz, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short', ...(options || {}),
  });
}

/** The company-zone calendar day for a moment, as 'YYYY-MM-DD'. */
export function dayKeyInTz(value, tz) {
  const d = _toDate(value);
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolveTz(tz), year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t)?.value;
  return `${g('year')}-${g('month')}-${g('day')}`;
}

/** Local-midnight Date on the company's calendar day — safe for getDate()/setDate() math. */
export function companyToday(tz) {
  const [y, m, d] = dayKeyInTz(new Date(), tz).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Company-calendar 'YYYY-MM-DD' for today, shifted by `deltaDays`. */
export function todayInTz(deltaDays = 0, tz) {
  const d = companyToday(tz);
  d.setDate(d.getDate() + deltaDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "Sep 8, 2026" for a calendar date ('YYYY-MM-DD', never shifted) OR a timestamp (company zone). */
export function fmtDay(value, tz, options) {
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    return formatInTz(`${value}T12:00:00Z`, 'UTC', { year: 'numeric', month: 'short', day: 'numeric', ...(options || {}) });
  }
  return fmtDate(value, tz, options);
}

/** 'Today' / 'Yesterday' on the company's calendar, else null. */
export function relativeDayInTz(value, tz) {
  const k = dayKeyInTz(value, tz);
  if (!k) return null;
  const now = new Date();
  if (k === dayKeyInTz(now, tz)) return 'Today';
  if (k === dayKeyInTz(new Date(now.getTime() - 86400000), tz)) return 'Yesterday';
  return null;
}

/** Current hour (0-23) on the company clock. */
export function hourInTz(value, tz) {
  const h = Number(formatInTz(value || new Date(), tz, { hour: 'numeric', hourCycle: 'h23' }));
  return Number.isNaN(h) ? new Date().getHours() : h % 24;
}

/** Hook: the resolved company timezone for the signed-in tenant. */
export function useCompanyTz() {
  const { user } = useAuth();
  return resolveTz(user && user.company_timezone);
}
